import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { encolarAccion, esFaltaDeSenal } from '@/lib/campo/colaDeAcciones'
import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type {
  Herramienta,
  HerramientaPendiente,
  ResultadoDevolucion,
  TrasladoHerramienta,
} from '@/lib/supabase/tipos-bd'

/**
 * Las herramientas que se pueden mandar con una orden, y cuántas quedan.
 *
 * `cantidad_disponible` NO se calcula aquí ni se ajusta desde la aplicación:
 * la mantiene un disparador de la base contando las unidades que no están
 * prestadas. Lo único que hace esta consulta es leerla.
 */
export function useHerramientasDisponibles() {
  const consulta = useQuery({
    queryKey: ['herramientas', 'disponibles'],
    queryFn: async (): Promise<Herramienta[]> => {
      const { data, error } = await supabase
        .from('herramientas')
        .select('*')
        .eq('activo', true)
        .order('nombre')

      if (error) throw new Error(mensajeDeError(error))
      return data ?? []
    },
  })

  return {
    herramientas: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/**
 * Cuántas de cada tipo tiene apartadas una orden, para volver a marcar las
 * casillas al reabrir la programación.
 *
 * El préstamo es de UNIDADES concretas —cada una con su QR— pero quien
 * programa razona en tipos y cantidades: «dos arneses». Así que aquí se
 * agrupa. Es el único sitio donde hace falta traducir entre las dos formas de
 * mirar lo mismo.
 */
export function useHerramientasDeLaOrden(ordenTrabajoId: string | null) {
  const consulta = useQuery({
    queryKey: ['herramientas', 'de-la-orden', ordenTrabajoId],
    enabled: !!ordenTrabajoId,
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from('prestamos_herramienta')
        .select('unidad:unidades_herramienta!inner(herramienta_id)')
        .eq('orden_trabajo_id', ordenTrabajoId!)
        .is('devuelta_en', null)

      if (error) throw new Error(mensajeDeError(error))

      const porTipo: Record<string, number> = {}
      for (const fila of data ?? []) {
        // La relación viene como objeto con !inner, pero PostgREST la tipa
        // como si pudiera ser lista: se normaliza aquí y no en cada uso.
        const unidad = Array.isArray(fila.unidad) ? fila.unidad[0] : fila.unidad
        if (!unidad) continue
        porTipo[unidad.herramienta_id] = (porTipo[unidad.herramienta_id] ?? 0) + 1
      }
      return porTipo
    },
  })

  return { yaTiene: consulta.data ?? {}, cargando: consulta.isLoading }
}

/**
 * 0318: Las herramientas que el técnico sugirió en la cotización de origen de la OT.
 */
export function useHerramientasRequeridas(ordenTrabajoId: string | null) {
  const consulta = useQuery({
    queryKey: ['herramientas', 'requeridas-ot', ordenTrabajoId],
    enabled: Boolean(ordenTrabajoId),
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase.rpc('fn_herramientas_requeridas_ot', {
        p_orden_trabajo_id: ordenTrabajoId!,
      })

      if (error) return {}
      return (data as Record<string, number>) ?? {}
    },
  })

  return { requeridas: consulta.data ?? {}, cargando: consulta.isLoading }
}

export type Eleccion = Record<string, number>

/**
 * Asignar.
 *
 * Manda la lista COMPLETA de tipos y cantidades, no las diferencias: la
 * función de la base deja la orden igual a lo que reciba y ella elige qué
 * unidades apartar. Calcular aquí qué se añadió y qué se quitó sería repetir
 * en el navegador una cuenta que la base ya hace, y con dos personas
 * programando a la vez las dos cuentas dejarían de coincidir.
 */
export function useAsignarHerramientas() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      eleccion,
    }: {
      ordenTrabajoId: string
      eleccion: Eleccion
    }) => {
      const { error } = await supabase.rpc('fn_asignar_herramientas', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_herramientas: Object.entries(eleccion)
          .filter(([, cantidad]) => cantidad > 0)
          .map(([herramienta_id, cantidad]) => ({ herramienta_id, cantidad })),
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar(clienteConsultas),
  })
}

/**
 * Devolver leyendo el QR, estando en la bodega.
 *
 * Puede responder «confirmar» en vez de dar por devuelta la herramienta. Eso
 * NO es un fallo: es que la unidad escaneada no es la que estaba apartada, y
 * el sistema no puede saber si el técnico cogió otra del estante o si escaneó
 * dos veces. Quien está ahí sí lo sabe, así que se le pregunta.
 */
export function useDevolverPorQr() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      token,
      posicion,
      novedad,
      confirmarCambio = false,
    }: {
      token: string
      posicion: { latitud: number; longitud: number; exactitud?: number | null }
      novedad?: string
      confirmarCambio?: boolean
    }): Promise<ResultadoDevolucion> => {
      const argumentos = {
        p_token: token,
        p_latitud: posicion.latitud,
        p_longitud: posicion.longitud,
        p_exactitud_m: posicion.exactitud ?? null,
        p_novedad: novedad?.trim() || null,
        p_confirmar_cambio: confirmarCambio,
      }
      /*
       * SIN SEÑAL SE GUARDA CON LA UBICACIÓN DE AHORA. La base comprueba que
       * el escaneo se hizo en la bodega, y lo que importa es dónde estaba el
       * teléfono al escanear, no al enviar: viaja con la lectura del GPS de
       * este momento. La llave es el código del QR, para no guardarla dos
       * veces si la escanea otra vez.
       */
      const encolar = async (): Promise<ResultadoDevolucion> => {
        await encolarAccion('devolucion', token, argumentos)
        return {
          estado: 'en_cola',
          etiqueta: '',
          herramienta: '',
          orden_numero: '',
          mensaje: 'Quedó guardada en el teléfono. Se entrega sola en cuanto haya señal.',
        }
      }
      if (esFaltaDeSenal(null)) return encolar()

      const { data, error } = await supabase.rpc('fn_devolver_por_qr', argumentos)

      if (error && esFaltaDeSenal(error)) return encolar()
      if (error) throw new Error(mensajeDeError(error))
      return data[0]
    },
    onSuccess: refrescar(clienteConsultas),
  })
}

/**
 * Recibir sin escanear.
 *
 * La salida de emergencia para cuando el GPS no alcanza bajo el techo de la
 * bodega, o la etiqueta está ilegible. Exige motivo y queda marcada como
 * manual: si con el tiempo la mayoría de las devoluciones son manuales, la
 * geocerca está mal puesta y se va a ver contando.
 */
export function useDevolverAMano() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({ prestamoId, motivo }: { prestamoId: string; motivo: string }) => {
      const { error } = await supabase.rpc('fn_devolver_a_mano', {
        p_prestamo_id: prestamoId,
        p_motivo: motivo.trim(),
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar(clienteConsultas),
  })
}

// ─── Traslado de herramienta entre órdenes (con aprobación) ──────────────────

export type OrdenActiva = {
  id: string
  numero: string
  cliente: string | null
  direccion: string | null
}

/** Las órdenes activas, para elegir el destino de un traslado. */
export function useOrdenesActivas() {
  const consulta = useQuery({
    queryKey: ['herramientas', 'ordenes-activas'],
    // Con respaldo: el formulario de traslado se llena en la obra.
    queryFn: () =>
      conRespaldo<OrdenActiva[]>('ordenes-activas', async () => {
        const { data, error } = await supabase
          .from('ordenes_trabajo')
          .select(
            'id, numero, orden_servicio:ordenes_servicio(direccion, cliente:clientes(nombre))',
          )
          .in('estado', ['programada', 'en_ejecucion', 'en_precierre'])
          .order('numero', { ascending: false })

        if (error) throw new Error(mensajeDeError(error))

        return (data ?? []).map((o) => {
          const os = Array.isArray(o.orden_servicio) ? o.orden_servicio[0] : o.orden_servicio
          const cli = os && (Array.isArray(os.cliente) ? os.cliente[0] : os.cliente)
          return {
            id: o.id,
            numero: o.numero,
            cliente: cli?.nombre ?? null,
            direccion: os?.direccion ?? null,
          }
        })
      }),
  })

  return { ordenes: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Los traslados (pendientes e historial), con nombres resueltos. */
export function useTraslados() {
  const consulta = useQuery({
    queryKey: ['herramientas', 'traslados'],
    queryFn: async (): Promise<TrasladoHerramienta[]> => {
      const { data, error } = await supabase
        .from('vista_traslados_herramienta')
        .select('*')
        .order('solicitado_en', { ascending: false })

      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as unknown as TrasladoHerramienta[]
    },
  })

  return { traslados: consulta.data ?? [], cargando: consulta.isLoading }
}

/**
 * Solicitar el traslado de una herramienta: a otra orden, o a otra persona.
 *
 * LOS DOS DESTINOS PASAN POR LO MISMO —la aprobación de gerencia— porque los
 * dos hacen lo mismo: mueven de encima de alguien la responsabilidad de una
 * herramienta. Devolverla a bodega no pide aprobación, y por eso no está aquí:
 * ahí la responsabilidad no se traspasa, se termina.
 */
export function useSolicitarTraslado() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      prestamoId,
      ordenDestinoId,
      colaboradorDestinoId,
      motivo,
    }: {
      prestamoId: string
      ordenDestinoId?: string | null
      colaboradorDestinoId?: string | null
      motivo: string
    }) => {
      const argumentos = {
        p_prestamo_origen_id: prestamoId,
        p_motivo: motivo.trim(),
        p_orden_destino_id: ordenDestinoId ?? null,
        p_colaborador_destino_id: colaboradorDestinoId ?? null,
      }
      // Sin señal se guarda y sale sola: la llave es el préstamo.
      if (esFaltaDeSenal(null)) {
        await encolarAccion('traslado', prestamoId, argumentos)
        return { enCola: true }
      }

      const { error } = await supabase.rpc('fn_solicitar_traslado_herramienta', argumentos)

      if (error && esFaltaDeSenal(error)) {
        await encolarAccion('traslado', prestamoId, argumentos)
        return { enCola: true }
      }
      if (error) throw new Error(mensajeDeError(error))
      return { enCola: false }
    },
    onSuccess: refrescar(clienteConsultas),
  })
}

/**
 * Lo que sigue afuera a nombre de una orden.
 *
 * Es la pregunta de la pantalla de cierre y no existía: la vista de calle trae
 * todo lo de la empresa y nadie la filtraba por orden, así que justo donde hay
 * que decidir qué pasa con cada herramienta no se veía ninguna.
 */
export function useHerramientasPendientes(ordenTrabajoId: string | null) {
  const consulta = useQuery({
    queryKey: ['herramientas', 'pendientes', ordenTrabajoId],
    enabled: Boolean(ordenTrabajoId),
    queryFn: async (): Promise<HerramientaPendiente[]> => {
      const { data, error } = await supabase.rpc('fn_herramientas_de_la_orden', {
        p_orden_trabajo_id: ordenTrabajoId!,
      })

      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as unknown as HerramientaPendiente[]
    },
  })

  return { pendientes: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Los técnicos a los que se les puede pasar una herramienta. */
export function useColaboradoresActivos() {
  const consulta = useQuery({
    queryKey: ['herramientas', 'colaboradores'],
    queryFn: () =>
      conRespaldo<
        { id: string; nombre: string; cargo: string | null; tarjeta_conte: boolean | null }[]
      >('colaboradores-activos', async () => {
        const { data, error } = await supabase
          .from('colaboradores')
          // La matrícula CONTE viaja siempre: es la que dice quién puede firmar
          // un RETIE, y estaba en la base sin que ninguna pantalla la mostrara.
          .select('id, nombre, cargo, tarjeta_conte')
          .eq('activo', true)
          .order('nombre')

        if (error) throw new Error(mensajeDeError(error))
        return data ?? []
      }),
  })

  return { colaboradores: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Aprobar o rechazar un traslado (solo gerencia). */
export function useDecidirTraslado() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      trasladoId,
      aprobar,
      notas,
    }: {
      trasladoId: string
      aprobar: boolean
      notas?: string
    }) => {
      const { error } = await supabase.rpc('fn_decidir_traslado_herramienta', {
        p_traslado_id: trasladoId,
        p_aprobar: aprobar,
        p_notas: notas?.trim() || null,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar(clienteConsultas),
  })
}

/** Todo lo de herramientas se refresca junto: una devolución mueve tres vistas. */
function refrescar(cliente: ReturnType<typeof useQueryClient>) {
  return async () => {
    await cliente.invalidateQueries({ queryKey: ['herramientas'] })
    // Y el tablero, donde ahora también se decide (0238).
    await cliente.invalidateQueries({ queryKey: ['lo-que-espera'] })
    await cliente.invalidateQueries({ queryKey: ['mis-herramientas'] })
    await cliente.invalidateQueries({ queryKey: ['maestro', 'herramientas'] })
  }
}
